import { DatTypeEnum as datType, DatRelatedV3 } from 'ctp-toll-domain';
import { MasterAccountInfo } from 'ctp-user-auth-service';
import moment from 'moment';
import { DatsState, DatFlagEnum, DatStateEnum, INotification } from './types';

interface DatStatus {
  state: DatStateEnum,
  flag: DatFlagEnum
}

/**
 * List all the possible states (key) of a DAT and their conditions (value as function: boolean)
 * Archiving requested takes precedence over the other states (Map order is preserved)
 * Deleted will never be used because a deleted DAT will not appear in the list
 * Archived is temporarily unused because no flag is set in the backend at the moment
 * See https://wiki.int.be.continental.cloud/pages/viewpage.action?pageId=1126105266 for detailed conditions
 */
const datStateMap = new Map([
  ['inactive', (dat: DatRelatedV3) => dat.datVersions?.length === 1 && !dat.datVersions[0].validFrom],
  ['pending', (dat: DatRelatedV3) => dat.datVersions && dat.datVersions?.length > 0 && dat.datVersions[0].validFrom && dat.datVersions?.every(version => moment.unix(version.validFrom).isAfter(moment()))],
  ['active', (dat: DatRelatedV3) => dat.datVersions && dat.datVersions?.length > 0 && dat.datVersions[0].validFrom && dat.datVersions?.some(version => moment.unix(version.validFrom).isSameOrBefore(moment()))],
  ['requested', (dat: DatRelatedV3) => !!dat.validTo],
  ['archived', (dat: DatRelatedV3) => dat.name === 'archived'],
  ['deleted', (dat: DatRelatedV3) => dat.name === 'deleted']
]);

/**
 * Filters a map object and returns the state that is true
 * A DAT can be active with a request to be archived, so 'requested' is last in the loop
 * Note: A method Map.GroupBy exists but is not fully supported by all browsers
 * @param dat Dat
 * @returns state string The key that represents the state of the DAT (from the map) 'Inactive' by default
 */
const getCurrentState = (dat: DatRelatedV3) => {
  let state;
  datStateMap.forEach((_, k, self) => {
    if (self.get(k)!(dat)) {
      state = k;
    }
  });
  return state || 'inactive';
};

const getters = {
  /**
   * Returns the different states of a DAT depending on conditions defined in
   * https://wiki.int.be.continental.cloud/pages/viewpage.action?pageId=1126105266
   * @param dat the DAT object
   * @returns DatStatus
   */
  getDatStatus: () => (dat: DatRelatedV3): DatStatus => {
    const isUsed = dat.instances?.length && dat.instances.length > 0 ? DatFlagEnum.Used : DatFlagEnum.Unused;
    const datStatus: DatStatus = { state: getCurrentState(dat) as DatStateEnum, flag: isUsed };
    return datStatus;
  },
  getCCCDats: (state: DatsState) => (master: MasterAccountInfo) => state.dats.filter(dat => dat.type === datType.Ccc && dat.masterId === master.id),
  getEFCDats: (state: DatsState) => (master: MasterAccountInfo) => state.dats.filter(dat => dat.type === datType.Efc && dat.masterId === master.id),
  getLACDats: (state: DatsState) => (master: MasterAccountInfo) => state.dats.filter(dat => dat.type === datType.Lac && dat.masterId === master.id),
  getAllDats: (state: DatsState) => (master: MasterAccountInfo) => state.dats.filter(dat => dat.masterId === master.id),
  getDatsState(state: DatsState) {
    return state.datsState;
  },
  getDats(state: DatsState) {
    return state.dats;
  },
  getErrorMessage(state: DatsState): string {
    return state.errorMessage;
  },
  getNotification(state: DatsState): INotification | undefined {
    return state.notification;
  }
};

export default getters;
